import { useCallback, useEffect, useRef, useState } from 'react';

import { SlotActions } from '@Actions/SlotActions';
import { NotificationType, showNotification } from '@Components/design-system/notification/notification';
import { IQueryData, updateLocationCode, updateValidUserCity } from '@Reducers/common';
import { updateSortedClinicLocations } from '@Reducers/location';
import { IClinicLocationsData } from '@Reducers/schedule';
import { IUserData } from '@Reducers/user';
import { useAppDispatch, useAppSelector } from '@Redux/hooks';
import {
  CONSULTATION_TYPE,
  DEFAULT_LOCATION_CODE,
  INSTANT_LOCATION_CODE,
  PHONE_CALL_EXPERIMENT_PROVIDERS,
  PHONE_CALL_LOCATION_CODE,
} from '@Utils/constants';
import { clarityLogger, isOfflineLocation } from '@Utils/helper';
import { sortCitiesByClinicDistance, sortClinicsByDistance } from '@Utils/Helpers/location/helper';
import useAnasRouter from '@Utils/hooks/anas-router';
import { Routes } from '@Utils/hooks/anas-router/routes';
import usePosthog, { CtaEvents } from '@Utils/hooks/posthog';

import useBrowserLocation from '../../browser-location';
import { Direction } from '../constants';
import { fallsUnderInstant } from './initial';
import useOnEnter from './on-enter';
import { Rule } from './type';

export const useMeduimRule = (): Rule => {
  const dispatch = useAppDispatch();
  const router = useAnasRouter();
  const userPosition = useBrowserLocation(true);
  const ipCity: string = useAppSelector((state) => state.user?.ipCity.city);
  const userData: IUserData = useAppSelector((state) => state.user.userData);
  const queryData: IQueryData = useAppSelector((state) => state.common.queryData);
  const queryRef = useRef(queryData);
  queryRef.current = queryData;
  const clinicLocations: Record<string, IClinicLocationsData[]> = useAppSelector(
    (state) => state.location?.offlineClinicLocations,
  );
  const [isLoading, setIsLoading] = useState(false);
  const { ctaEventCapture } = usePosthog();

  const next = useCallback(() => {
    ctaEventCapture(CtaEvents.MEDIUM);
    switch (true) {
      // if location code is not set, route to select city
      case !queryData?.locationCode && !queryData?.validUserCity:
        router.push({ route: Routes.APPOINTMENT, subRoute: Direction.SELECT_CITY });
        break;
      // if instant flow is true, navigate to checkout
      case queryData?.locationCode === INSTANT_LOCATION_CODE && fallsUnderInstant():
        router.push({ route: Routes.APPOINTMENT, subRoute: Direction.SELECT_LANGUAGE });
        dispatch(SlotActions.ResetSlots());
        break;
      // if location code is set to online, route to select language
      case queryData?.locationCode === PHONE_CALL_LOCATION_CODE &&
        !userData?.physicianId &&
        !queryData?.providerId?.[0]:
      case queryData?.locationCode === DEFAULT_LOCATION_CODE && !userData?.physicianId && !queryData?.providerId?.[0]:
        router.push({ route: Routes.APPOINTMENT, subRoute: Direction.SELECT_LANGUAGE });
        break;
      // if location code is set to online and physician assigned, route to select slot
      case queryData?.locationCode === PHONE_CALL_LOCATION_CODE &&
        (!!userData?.physicianId || !!queryData?.providerId?.[0]):
      case queryData?.locationCode === DEFAULT_LOCATION_CODE &&
        (!!userData?.physicianId || !!queryData?.providerId?.[0]):
        router.push({ route: Routes.APPOINTMENT, subRoute: Direction.SELECT_SLOT });
        break;
      // if location code is set to offline and there is only one clinic, route to select slot
      case queryData?.locationCode !== DEFAULT_LOCATION_CODE && Object.values(clinicLocations).flat().length === 1:
        if (!queryData?.locationCode)
          dispatch(updateLocationCode(clinicLocations[Object.keys(clinicLocations)?.[0]]?.[0]?.code));
        router.push({ route: Routes.APPOINTMENT, subRoute: Direction.SELECT_SLOT });
        break;
      case !queryData?.locationCode:
        router.push({ route: Routes.APPOINTMENT, subRoute: Direction.SELECT_CITY });
        break;
      // if location code is set to offline, route to select clinic
      case isOfflineLocation(queryData?.locationCode):
        router.push({ route: Routes.APPOINTMENT, subRoute: Direction.SELECT_CLINIC });
        break;
      default:
        showNotification(NotificationType.ERROR, 'Please select an option', 3000);
    }
  }, [queryData]);

  const updateLocation = useCallback(() => {
    setIsLoading(true);
    const sortedClinics = userPosition ? sortClinicsByDistance(clinicLocations, userPosition) : clinicLocations;
    dispatch(updateSortedClinicLocations(sortedClinics));
    const clinicCities = sortCitiesByClinicDistance(sortedClinics);
    const isIpCityValid = clinicCities.find((city) => city.toLowerCase() === ipCity?.toLowerCase());
    const isUserCityValid = clinicCities.find(
      (city) => city.toLowerCase() === queryRef.current?.validUserCity?.toLowerCase(),
    );
    const nearestCity =
      userPosition && sortedClinics?.[clinicCities?.[0]]?.[0]?.distance <= 100 ? clinicCities?.[0] : isIpCityValid;
    const nearestClinic =
      userPosition && sortedClinics?.[clinicCities?.[0]]?.[0]?.distance <= 100
        ? sortedClinics?.[clinicCities?.[0]]?.[0]
        : undefined;

    if (Object.values(sortedClinics).flat().length === 1) {
      dispatch(updateValidUserCity(clinicCities?.[0]));
      dispatch(updateLocationCode(sortedClinics?.[clinicCities?.[0]]?.[0].code));
    } else {
      if (nearestCity && !isUserCityValid) dispatch(updateValidUserCity(nearestCity));
    }
    if (queryData?.consultationCode === CONSULTATION_TYPE.FU) {
      dispatch(updateLocationCode(nearestCity ? clinicLocations[nearestCity]?.[0]?.code : DEFAULT_LOCATION_CODE));
    } else if (nearestClinic) {
      dispatch(updateLocationCode(nearestClinic.code));
    } else if (!isIpCityValid) {
      dispatch(updateLocationCode(DEFAULT_LOCATION_CODE));
    }
    setIsLoading(false);
  }, [dispatch, userPosition, ipCity, clinicLocations]);

  //not adding clinicLocations to the dependency its leading to infinite loop (its loaded in previous step)(infinte deps loop)
  useOnEnter(() => {
    if (queryData?.locationCode) return;
    updateLocation();
  }, [dispatch, userPosition, queryData?.locationCode, ipCity]);

  //log phone call experiment eligible users on clarity
  useEffect(() => {
    if (PHONE_CALL_EXPERIMENT_PROVIDERS.includes(userData?.physicianId)) clarityLogger('phone_call', 'true');
  }, [userData?.physicianId]);

  return {
    next: next,
    isLoading: isLoading,
  };
};
